import { EnvironmentService } from '../environment';

interface HistoryServiceOptions {
    environmentService: EnvironmentService;
    handler: () => void;
}

export interface HistoryService {
    goBack: () => void;
    handleLeaving: () => void;
    startTracking: () => void;
    stopTracking: () => void;
    pushState: (state: object, url?: string) => void;
}

const createHistoryService = ({ handler, environmentService }: HistoryServiceOptions): HistoryService => {
    let historyTracked = false;

    const wrappedHandler = () => {
        handler();
        if (historyTracked) {
            insertHistoryBreakpoint();
        }
    };

    const startTracking = () => {
        if (environmentService.hasIn('history') && !historyTracked) {
            window.setTimeout(() => {
                environmentService.invokeArgs('addEventListener', ['popstate', wrappedHandler]);
            });

            const history = environmentService.get('history');

            if (!history?.state?.historyTracked) {
                insertHistoryBreakpoint();
            }

            historyTracked = true;
        }
    };

    const insertHistoryBreakpoint = () => {
        environmentService.invokeArgs(['history', 'pushState'], [{ historyTracked: true }, null]);
    };

    const goBack = () => {
        environmentService.invoke(['history', 'back']);
    };

    const stopTracking = () => {
        if (historyTracked) {
            environmentService.invokeArgs('removeEventListener', ['popstate', wrappedHandler]);
            historyTracked = false;
        }
    };

    const handleLeaving = () => {
        const history = environmentService.get('history');
        if (history?.length === 2) {
            // if the history is empty, there is no leave happening, so restart the tracking
            startTracking();
        } else {
            goBack();
        }
    };

    const pushState = (state: object, url?: string | URL) => {
        if (url != null && url !== '') {
            environmentService.invokeArgs(['history', 'pushState'], [state, null, url]);
        } else {
            environmentService.invokeArgs(['history', 'pushState'], [state, null]);
        }
    };

    return {
        startTracking,
        stopTracking,
        handleLeaving,
        goBack,
        pushState,
    };
};

export default createHistoryService;
